/**
 * DataSource 域稳定常量与轻量映射。
 */

import { FieldType } from './types';
import type { TFieldType } from './types';

/** 字段类型与 UI 图标的映射。 */
export const FieldTypeIcon: Record<TFieldType, string> = {
  [FieldType.Date]: 'date',
  [FieldType.Time]: 'time',
  [FieldType.String]: 'abc',
  [FieldType.Number]: 'a-123',
  [FieldType.BigInt]: 'a-123',
  [FieldType.Decimal]: 'a-123',
  [FieldType.Float]: 'a-123',
  [FieldType.Percent]: 'a-123',
};

/** 指标聚合方式合法取值（value 与后端 aggregate 协议一致）。 */
export const AggregateType = {
  Sum: 'sum',
  Avg: 'avg',
  Max: 'max',
  Min: 'min',
  Avgpp: 'avgpp',
  Distinct: 'distinct',
  Count: 'count',
  Variance: 'variance',
  Std: 'std',
  Aggregated: '',
} as const;

export type TAggregateType =
  (typeof AggregateType)[keyof typeof AggregateType];

/**
 * 指标聚合方式 → 中英文展示文案（metric 下拉、NoticeBuilder 等）。
 *
 * @core SortableDrag 指标编辑、探索分析、AB 实验共用。
 */
export const MetricTypes = {
  sum: {
    text: '总和',
    text_en: 'Sum',
    value: AggregateType.Sum,
  },
  avg: {
    text: '均值',
    text_en: 'AVG',
    value: AggregateType.Avg,
  },
  max: {
    text: '最大值',
    text_en: 'Max',
    value: AggregateType.Max,
  },
  min: {
    text: '最小值',
    text_en: 'Min',
    value: AggregateType.Min,
  },
  avgpp: {
    text: '人均值',
    text_en: 'Average',
    value: AggregateType.Avgpp,
  },
  distinct: {
    text: '去重数',
    text_en: 'Distinct',
    value: AggregateType.Distinct,
  },
  count: {
    text: '计数',
    text_en: 'Count',
    value: AggregateType.Count,
  },
  variance: {
    text: '方差',
    text_en: 'Variance',
    value: AggregateType.Variance,
  },
  std: {
    text: '标准差',
    text_en: 'Standard',
    value: AggregateType.Std,
  },
  aggregated: {
    text: '已聚合',
    text_en: 'Aggregated',
    value: AggregateType.Aggregated,
  },
} as const;

export const MetricOptions = [
  MetricTypes.sum,
  MetricTypes.avg,
  MetricTypes.max,
  MetricTypes.min,
  MetricTypes.distinct,
  MetricTypes.count,
  MetricTypes.aggregated,
];

export interface IMetricOptionItem {
  value: string;
  text: string;
  disabled?: boolean;
}

/** 按 metric_method 返回可选聚合方式列表。 */
export const getMetricOptions = (
  metricMode: number,
  isEn: boolean,
): IMetricOptionItem[] => {
  const options: IMetricOptionItem[] = [];
  switch (metricMode) {
    case 1:
      break;
    case 2:
      options.push({
        value: MetricTypes.distinct.value,
        text: MetricTypes.distinct[isEn ? 'text_en' : 'text'],
      });
      break;
    case 3:
      options.push(
        {
          value: MetricTypes.count.value,
          text: MetricTypes.count[isEn ? 'text_en' : 'text'],
        },
        {
          value: MetricTypes.distinct.value,
          text: MetricTypes.distinct[isEn ? 'text_en' : 'text'],
        },
      );
      break;
    case 4:
      [
        MetricTypes.sum,
        MetricTypes.avg,
        MetricTypes.max,
        MetricTypes.min,
        MetricTypes.avgpp,
        MetricTypes.distinct,
        MetricTypes.variance,
        MetricTypes.std,
      ].forEach(opt => {
        options.push({ value: opt.value, text: isEn ? opt.text_en : opt.text });
      });
      break;
    case 5:
      [
        MetricTypes.sum,
        MetricTypes.avg,
        MetricTypes.max,
        MetricTypes.min,
        MetricTypes.count,
      ].forEach(opt => {
        options.push({ value: opt.value, text: isEn ? opt.text_en : opt.text });
      });
      break;
    default:
      break;
  }
  return options;
};

export const CustomExMetricOptions: IMetricOptionItem[] = [
  {
    text: '-',
    value: '',
    disabled: false,
  },
];

export const DisplayValueList: string[] = ['sum', 'avg'];
export const SensitiveDisplayValueList: string[] = ['distinct', 'count'];
export const DimNumberTrendDisplayValueList: string[] = [
  'avg',
  'max',
  'min',
  'avgpp',
];
